import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  smartSearch,
  splitByMatches,
  highlightTokensInText,
  type SearchableField,
  type MatchRange,
  type SearchResult,
} from "@/shared/utils/smartSearch";

// ── Types ──────────────────────────────────────────────────────────

export interface ComboBoxProps<T> {
  id?: string;
  items: T[];
  value: string | null;
  onChange: (value: string) => void;
  /** Search field extractors passed to smartSearch */
  fields: ((item: T) => SearchableField)[];
  /** Unique key for each item */
  getKey: (item: T) => string;
  /** Display label when item is selected (shown in the input) */
  getDisplayLabel: (item: T) => string;
  /** Render a single option row. Receives match ranges and tokens for highlighting. */
  renderItem?: (
    item: T,
    highlighted: boolean,
    matches: Map<number, MatchRange[]>,
    tokens?: string[],
  ) => React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  error?: string;
  className?: string;
  onBlur?: () => void;
  /** Max results to show (default 50) */
  limit?: number;
  /** Max items to show when there's no search query (defaults to `limit`). Useful for hiding less-common items until the user searches. */
  defaultLimit?: number;
  /** Show clear (X) button when a value is selected */
  clearable?: boolean;
  onClear?: () => void;
  /** Text to pre-fill when the input is focused via Tab (defaults to getDisplayLabel). Use a shorter value to improve search results. */
  getSearchValue?: (item: T) => string;
  /** Render a rich display inside the input when an item is selected (replaces the flat placeholder text) */
  renderSelected?: (item: T) => React.ReactNode;
  /** Width of the dropdown (default: match input width) */
  dropdownWidth?: string;
  /** Override the items shown when the query is empty. Defaults to all items (capped by limit). */
  getDefaultItems?: (items: T[]) => T[];
  /** Override the search-with-query behavior. When set, replaces smartSearch. */
  searchItems?: (items: T[], query: string) => T[];
}

// ── Component ──────────────────────────────────────────────────────

export function ComboBox<T>({
  id,
  items,
  value,
  onChange,
  fields,
  getKey,
  getDisplayLabel,
  renderItem,
  placeholder = "Search...",
  disabled = false,
  disabledMessage,
  error,
  className,
  onBlur,
  limit = 50,
  defaultLimit,
  clearable = false,
  onClear,
  getSearchValue,
  renderSelected,
  dropdownWidth,
  getDefaultItems,
  searchItems,
}: ComboBoxProps<T>) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  // Tracks whether onFocus just fired from the same click — prevents onClick from toggling closed immediately
  const justFocusedRef = React.useRef(false);
  // Set by pointerdown (fires before focus) to distinguish click-focus from tab-focus
  const pointerFocusRef = React.useRef(false);

  const initialCap = defaultLimit ?? limit;

  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [visibleCount, setVisibleCount] = React.useState(initialCap);

  React.useEffect(() => {
    setVisibleCount(initialCap);
  }, [inputValue, items, initialCap]);

  // Find selected item
  const selectedItem = React.useMemo(() => {
    if (!value) return null;
    return items.find((item) => getKey(item) === value) ?? null;
  }, [items, value, getKey]);

  // When the selected item changes externally, update the input display
  const displayText = selectedItem ? getDisplayLabel(selectedItem) : "";

  // Search the full list, then slice to visibleCount so "Show more" preserves ranking
  const { results, totalAvailable } = React.useMemo<{
    results: SearchResult<T>[];
    totalAvailable: number;
  }>(() => {
    const wrap = (arr: T[]): SearchResult<T>[] =>
      arr.map((item) => ({
        item,
        score: 0,
        matches: new Map<number, MatchRange[]>(),
        matchedTokens: [] as string[],
      }));

    if (!inputValue.trim()) {
      const base = getDefaultItems ? getDefaultItems(items) : items;
      return { results: wrap(base.slice(0, visibleCount)), totalAvailable: base.length };
    }
    if (searchItems) {
      const full = searchItems(items, inputValue);
      return { results: wrap(full.slice(0, visibleCount)), totalAvailable: full.length };
    }
    const full = smartSearch(items, inputValue, fields, { limit: items.length });
    return { results: full.slice(0, visibleCount), totalAvailable: full.length };
  }, [items, inputValue, fields, visibleCount, getDefaultItems, searchItems]);

  const hasMore = results.length < totalAvailable;
  const showMoreIndex = results.length;

  const handleShowMore = React.useCallback(() => {
    setVisibleCount((c) => Math.min(c + initialCap, totalAvailable));
  }, [initialCap, totalAvailable]);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const activeEl = listRef.current.children[activeIndex] as HTMLElement;
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // ── Handlers ───────────────────────────────────────────────────

  const openDropdown = React.useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(-1);
  }, [disabled]);

  const closeDropdown = React.useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const selectItem = React.useCallback(
    (item: T) => {
      const key = getKey(item);
      onChange(key);
      setInputValue("");
      closeDropdown();
    },
    [getKey, onChange, closeDropdown],
  );

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setActiveIndex(-1);
      if (!open) openDropdown();
    },
    [open, openDropdown],
  );

  const handleInputFocus = React.useCallback(() => {
    justFocusedRef.current = true;
    if (pointerFocusRef.current) {
      // Click focus: show full list, no pre-fill
      pointerFocusRef.current = false;
    } else {
      // Tab focus: pre-fill + select-all so typing replaces
      if (selectedItem) {
        const prefill = getSearchValue ? getSearchValue(selectedItem) : getDisplayLabel(selectedItem);
        setInputValue(prefill);
        requestAnimationFrame(() => inputRef.current?.select());
      }
    }
    openDropdown();
  }, [selectedItem, getSearchValue, getDisplayLabel, openDropdown]);

  const handleInputClick = React.useCallback(() => {
    // If focus just fired from this same click, skip — onFocus already opened the dropdown
    if (justFocusedRef.current) {
      justFocusedRef.current = false;
      return;
    }
    // Already focused: toggle the dropdown
    if (open) {
      setInputValue("");
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [open, openDropdown, closeDropdown]);

  const handleInputBlur = React.useCallback(
    (e: React.FocusEvent) => {
      // Don't close if focus moved within the container (e.g. clicking an option)
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      setInputValue("");
      closeDropdown();
      onBlur?.();
    },
    [closeDropdown, onBlur],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          if (!open) {
            openDropdown();
          } else {
            const maxIndex = hasMore ? results.length : results.length - 1;
            setActiveIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
          }
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (!open) {
            openDropdown();
          } else {
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
          }
          break;
        }
        case "Enter": {
          e.preventDefault();
          if (open && activeIndex === showMoreIndex && hasMore) {
            handleShowMore();
            break;
          }
          if (open && results.length > 0) {
            const idx = activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
            selectItem(results[idx].item);
            // Defer focus advance until after React re-renders disabled state
            // changes from matter selection — otherwise newly enabled fields
            // are still marked disabled at query time and get skipped.
            // setTimeout(0) rather than requestAnimationFrame — rAF fires before
            // useEffect callbacks, so fields whose disabled state is derived in
            // an effect would still be stale at query time.
            setTimeout(() => {
              const container = inputRef.current?.closest("form") ?? inputRef.current?.closest("[role='dialog']");
              if (container) {
                const focusable = Array.from(
                  container.querySelectorAll<HTMLElement>(
                    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex="-1"])',
                  ),
                );
                const curIdx = focusable.indexOf(inputRef.current!);
                focusable[curIdx + 1]?.focus();
              }
            }, 0);
          }
          break;
        }
        case "Escape": {
          if (open) {
            e.preventDefault();
            e.stopPropagation();
            setInputValue("");
            closeDropdown();
          }
          break;
        }
        case "Tab": {
          if (open && results.length > 0) {
            const idx = activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
            selectItem(results[idx].item);
          } else {
            setInputValue("");
            closeDropdown();
          }
          break;
        }
      }
    },
    [disabled, open, activeIndex, results, hasMore, showMoreIndex, handleShowMore, openDropdown, closeDropdown, selectItem],
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
      setInputValue("");
      closeDropdown();
      inputRef.current?.blur();
    },
    [onClear, closeDropdown],
  );

  const handleOptionMouseDown = React.useCallback(
    (e: React.MouseEvent, item: T) => {
      // Prevent blur before selection completes
      e.preventDefault();
      selectItem(item);
      inputRef.current?.blur();
    },
    [selectItem],
  );

  // ── Default item renderer ──────────────────────────────────────

  const defaultRenderItem = React.useCallback(
    (item: T, _highlighted: boolean, _matches: Map<number, MatchRange[]>, tokens?: string[]) => {
      const label = getDisplayLabel(item);
      if (!tokens?.length) return label;

      // Highlight all token occurrences against the display label
      const ranges = highlightTokensInText(label, tokens);
      if (!ranges.length) return label;

      const parts = splitByMatches(label, ranges);
      return (
        <>
          {parts.map((part, i) =>
            part.highlighted ? (
              <mark key={i} className="bg-primary/20 text-inherit rounded-sm">
                {part.text}
              </mark>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </>
      );
    },
    [getDisplayLabel],
  );

  const renderer = renderItem ?? defaultRenderItem;

  // ── Render ─────────────────────────────────────────────────────

  const isSearching = inputValue.trim().length > 0;

  return (
    <div ref={containerRef} className={cn("relative min-w-0", className)}>
      {/* Input trigger */}
      <div
        className={cn(
          "relative flex min-w-0 items-center gap-1 rounded-md border-[1.25px] px-3 py-2 text-sm",
          renderSelected && "min-h-[58px]",
          "bg-card border-border text-foreground",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-red-500 bg-red-50 dark:border-red-700 dark:bg-red-900/40",
        )}
      >
        {/* Rich selected display — stays in flow for consistent height */}
        {renderSelected && selectedItem && (
          <div
            className={cn(
              "flex-1 min-w-0 overflow-hidden",
              open || isSearching ? "invisible" : "cursor-default",
            )}
            onPointerDown={() => { pointerFocusRef.current = true; }}
            onClick={() => inputRef.current?.focus()}
          >
            {renderSelected(selectedItem)}
          </div>
        )}

        {/* Input — overlays the spacer when renderSelected is active */}
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `combobox-option-${activeIndex}` : undefined
          }
          aria-invalid={!!error}
          disabled={disabled}
          placeholder={
            disabled && disabledMessage
              ? disabledMessage
              : open
                ? placeholder
                : selectedItem
                  ? displayText
                  : placeholder
          }
          value={isSearching || open ? inputValue : ""}
          onChange={handleInputChange}
          onPointerDown={() => { pointerFocusRef.current = true; }}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            "bg-transparent outline-none placeholder:text-muted-foreground min-w-0 text-ellipsis overflow-hidden",
            renderSelected && selectedItem
              ? cn("absolute inset-0 px-3 py-2", clearable && "pr-9", !open && !isSearching && "opacity-0 pointer-events-none")
              : cn("flex-1", !isSearching && !open && selectedItem && "placeholder:text-foreground"),
          )}
          autoComplete="off"
        />

        {/* Clear button */}
        {clearable && selectedItem && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={handleClear}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown listbox */}
      {open && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          className={cn(
            "absolute z-50 mt-1 w-full max-h-[300px] overflow-y-auto overflow-x-hidden",
            "rounded-md border-[1.25px] bg-card shadow-md",
            "border-border",
          )}
          style={dropdownWidth ? { width: dropdownWidth } : undefined}
        >
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              No results found.
            </li>
          ) : (
            <>
              {results.map((result, index) => {
                const key = getKey(result.item);
                const isActive = index === activeIndex;
                const isSelected = key === value;

                return (
                  <li
                    key={key}
                    id={`combobox-option-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(e) => handleOptionMouseDown(e, result.item)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center px-3 py-1.5 text-sm overflow-hidden",
                      "text-foreground",
                      isActive && "bg-accent text-accent-foreground",
                      !isActive && "hover:bg-accent/50",
                    )}
                  >
                    <div className="min-w-0 flex-1 overflow-hidden">
                      {renderer(result.item, isActive, result.matches, result.matchedTokens)}
                    </div>
                  </li>
                );
              })}
              {hasMore && (
                <li
                  id={`combobox-option-${showMoreIndex}`}
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => { e.preventDefault(); handleShowMore(); }}
                  onMouseEnter={() => setActiveIndex(showMoreIndex)}
                  className={cn(
                    "cursor-pointer select-none px-3 py-2 text-center text-sm border-t border-border",
                    activeIndex === showMoreIndex
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent/50",
                  )}
                >
                  Show more ({totalAvailable - results.length} remaining)
                </li>
              )}
            </>
          )}
        </ul>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export default ComboBox;
